'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
	updateWorkspaceImage,
	updateWorkspaceName,
} from '@/features/user-settings/services/workspace';

import { toast } from '@/shared/components/toast';
import { QUERY_KEY_INITIALS } from '@/shared/constants/query-key-initials';
import { tabStorage } from '@/shared/utils/tab-storage';

import {
	checkPageEditAccess,
	editCareersPage,
	fetchCareersDataClientSide,
} from '../services/careers-services';
import type { CareersData } from '../types/careers-types';

const MAX_PINNED_FORMS = 2;
const MAX_DESCRIPTION_LENGTH = 2000;
const AUTO_SAVE_DEBOUNCE_MS = 500;

const pinnedFormsKey = (ids: string[]) => ids.join(',');

type AutoSaveStatus = 'idle' | 'saving' | 'saved';

type CareersEditContextType = {
	// Data
	careersData: CareersData | undefined;
	isLoading: boolean;
	handle: string;

	// Auth
	hasEditAccess: boolean;
	isAccessLoading: boolean;
	pageId: string | null;

	// Edit mode
	isEditMode: boolean;

	// Edited values
	description: string;
	setDescription: (value: string) => void;
	setDescriptionFocused: (focused: boolean) => void;
	pinnedForms: string[];

	// Autosave
	autoSaveStatus: AutoSaveStatus;

	// Actions
	togglePin: (identifier: string) => void;

	// Name/Image (immediate save)
	handleNameSave: (name: string) => Promise<void>;
	isNameSaving: boolean;
	handleImageUpload: (file: File) => Promise<void>;
	handleImageRemove: () => Promise<void>;

	// Constants
	maxDescriptionLength: number;
	maxPinnedForms: number;
};

export const CareersEditContext = createContext<CareersEditContextType | null>(null);

type CareersEditProviderProps = {
	handle: string;
	children: React.ReactNode;
} & (
	| { mode: 'view'; isTokenExist: boolean; initialData: CareersData; embedded?: boolean }
	| { mode: 'edit'; pageId: string }
);

export function CareersEditProvider(props: CareersEditProviderProps) {
	const { handle, mode, children } = props;
	const queryClient = useQueryClient();

	// Extract mode-specific values cleanly (avoids `as` casts)
	const isTokenExist = mode === 'view' ? props.isTokenExist : false;
	const viewInitialData = mode === 'view' ? props.initialData : undefined;
	const editPageId = mode === 'edit' ? props.pageId : undefined;
	// Chromeless preview embed (e.g. agent sheet): never expose admin affordances,
	// even if a warm CHECK_PAGE_EDIT_ACCESS cache entry says the viewer has access.
	const embedded = mode === 'view' ? (props.embedded ?? false) : false;

	// --- Auth: check edit access (view mode only) ---
	const { data: accessData, isLoading: accessQueryLoading } = useQuery({
		queryKey: [QUERY_KEY_INITIALS.CHECK_PAGE_EDIT_ACCESS, handle],
		queryFn: () => checkPageEditAccess({ handle }),
		enabled: mode === 'view' && isTokenExist,
		retry: false,
	});

	const hasEditAccess =
		mode === 'edit' ? true : embedded ? false : (accessData?.hasAccess ?? false);
	const isAccessLoading = mode === 'edit' ? false : accessQueryLoading;
	const resolvedPageId = mode === 'edit' ? editPageId! : (accessData?.pageId ?? null);

	// Set workspace context so axios interceptor sends correct x-page-id for image/name APIs (edit mode only)
	useEffect(() => {
		if (mode === 'edit' && resolvedPageId) tabStorage.set('selectedWorkspaceId', resolvedPageId);
	}, [mode, resolvedPageId]);

	// --- Data: careers page data ---
	const careersQueryKey = useMemo(
		() => [QUERY_KEY_INITIALS.CAREERS_PAGE_DATA, handle] as const,
		[handle]
	);

	const { data: careersData, isLoading } = useQuery({
		queryKey: careersQueryKey,
		queryFn: () => fetchCareersDataClientSide(handle),
		...(viewInitialData ? { initialData: viewInitialData, initialDataUpdatedAt: Date.now() } : {}),
	});

	// --- Edit state ---
	// Local so the editor stays synchronous — a cache-backed controlled input re-renders a macrotask late and the caret jumps.
	const isEditMode = mode === 'edit';
	const [description, setDescriptionState] = useState(careersData?.description ?? '');
	const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);
	const [pinnedForms, setPinnedForms] = useState<string[]>(careersData?.pinnedForms ?? []);

	// Server snapshot the buffer was last seeded from: a refetch adopts it only where the user has no unsaved change, so invalidation lands without clobbering typing.
	const syncedServerRef = useRef<{ description: string; pinnedForms: string } | null>(null);
	const editedRef = useRef({ description, pinnedForms });
	editedRef.current = { description, pinnedForms };

	useEffect(() => {
		if (!careersData) return;

		const serverDescription = careersData.description ?? '';
		const serverPinnedForms = careersData.pinnedForms ?? [];
		const serverPinnedKey = pinnedFormsKey(serverPinnedForms);
		const synced = syncedServerRef.current;
		syncedServerRef.current = { description: serverDescription, pinnedForms: serverPinnedKey };

		if (!synced || editedRef.current.description === synced.description) {
			setDescriptionState(serverDescription);
		}
		if (!synced || pinnedFormsKey(editedRef.current.pinnedForms) === synced.pinnedForms) {
			setPinnedForms(serverPinnedForms);
		}
	}, [careersData]);

	// --- Autosave for description + pinned forms ---
	const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');
	const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	const { mutate: savePage } = useMutation({
		mutationFn: editCareersPage,
		onSuccess: (_, variables) => {
			// Mirror exactly what was saved, not the live buffer, which may already hold newer edits.
			queryClient.setQueryData(careersQueryKey, (prev: CareersData | undefined) =>
				prev
					? { ...prev, description: variables.description, pinnedForms: variables.pinned_forms }
					: prev
			);
			setAutoSaveStatus('saved');
			// Clear "saved" status after 3 seconds
			clearTimeout(savedStatusTimerRef.current);
			savedStatusTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 3000);
		},
		onError: () => {
			setAutoSaveStatus('idle');
		},
	});

	// Debounced autosave, gated on server data existing so a keystroke before the fetch lands can never post an empty page over it.
	useEffect(() => {
		if (!isEditMode || !resolvedPageId || !careersData) return;

		const serverDescription = careersData.description ?? '';
		const serverPinnedForms = careersData.pinnedForms ?? [];
		const descriptionToSave = isDescriptionFocused ? serverDescription : description;
		if (
			descriptionToSave === serverDescription &&
			pinnedFormsKey(pinnedForms) === pinnedFormsKey(serverPinnedForms)
		)
			return;

		clearTimeout(autoSaveTimerRef.current);
		autoSaveTimerRef.current = setTimeout(() => {
			setAutoSaveStatus('saving');
			savePage({
				page_id: resolvedPageId,
				description: isDescriptionFocused ? serverDescription : editedRef.current.description,
				pinned_forms: editedRef.current.pinnedForms,
			});
		}, AUTO_SAVE_DEBOUNCE_MS);

		return () => clearTimeout(autoSaveTimerRef.current);
	}, [
		careersData,
		description,
		isDescriptionFocused,
		isEditMode,
		pinnedForms,
		resolvedPageId,
		savePage,
	]);

	// Cleanup timers
	useEffect(() => {
		return () => {
			clearTimeout(autoSaveTimerRef.current);
			clearTimeout(savedStatusTimerRef.current);
		};
	}, []);

	// --- Name mutation (immediate save, used from WorkspaceNameDialog) ---
	const { mutateAsync: saveNameAsync, isPending: isNameSaving } = useMutation({
		mutationFn: updateWorkspaceName,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: careersQueryKey });
			toast({ title: 'Name updated', variant: 'success' });
		},
		onError: () => {
			toast({ title: 'Failed to update name', variant: 'destructive' });
		},
	});

	const handleNameSave = useCallback(
		async (name: string) => {
			if (!resolvedPageId) return;
			await saveNameAsync({ name, page_id: resolvedPageId });
		},
		[resolvedPageId, saveNameAsync]
	);

	// --- Pin toggle ---
	const togglePin = useCallback((identifier: string) => {
		setPinnedForms((prev) => {
			if (prev.includes(identifier)) return prev.filter((id) => id !== identifier);
			return prev.length >= MAX_PINNED_FORMS ? prev : [...prev, identifier];
		});
	}, []);

	// --- Image mutation (immediate save) ---
	const { mutateAsync: saveImageAsync } = useMutation({
		mutationFn: updateWorkspaceImage,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: careersQueryKey });
			toast({ title: 'Logo updated', variant: 'success' });
		},
		onError: () => {
			toast({ title: 'Failed to update logo', variant: 'destructive' });
		},
	});

	const handleImageUpload = useCallback(
		async (file: File) => {
			const formData = new FormData();
			formData.append('image', file);
			await saveImageAsync(formData);
		},
		[saveImageAsync]
	);

	const handleImageRemove = useCallback(async () => {
		const formData = new FormData();
		formData.append('remove', 'true');
		await saveImageAsync(formData);
	}, [saveImageAsync]);

	const setDescription = useCallback((value: string) => setDescriptionState(value), []);
	const setDescriptionFocused = useCallback(
		(focused: boolean) => setIsDescriptionFocused(focused),
		[]
	);

	const value = useMemo<CareersEditContextType>(
		() => ({
			careersData,
			isLoading,
			handle,
			hasEditAccess,
			isAccessLoading,
			pageId: resolvedPageId,
			isEditMode,
			description,
			setDescription,
			setDescriptionFocused,
			pinnedForms,
			autoSaveStatus,
			togglePin,
			handleNameSave,
			isNameSaving,
			handleImageUpload,
			handleImageRemove,
			maxDescriptionLength: MAX_DESCRIPTION_LENGTH,
			maxPinnedForms: MAX_PINNED_FORMS,
		}),
		[
			careersData,
			isLoading,
			handle,
			hasEditAccess,
			isAccessLoading,
			resolvedPageId,
			isEditMode,
			description,
			setDescription,
			setDescriptionFocused,
			pinnedForms,
			autoSaveStatus,
			togglePin,
			handleNameSave,
			isNameSaving,
			handleImageUpload,
			handleImageRemove,
		]
	);

	return <CareersEditContext.Provider value={value}>{children}</CareersEditContext.Provider>;
}
